import { useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { CanceledError } from "axios";
import { useErrorBoundary } from "react-error-boundary";
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from "@shared/lib/utils";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { Account, AccountSearch } from "@models/account";
import {
  buildAccountSort,
  SearchParams,
  searchAccounts,
} from "../api/accounts.service";
import {
  DEFAULT_SORT_KEY,
  parseAccountBrowseUrlParams,
  syncAccountBrowseUrlParams,
} from "../utils/accountBrowseUrlParams";
import { useEntityBrowse } from "./useEntityBrowse";

/**
 * Shared browse hook for account-type ticket pages (incomes, banks, etc.).
 */
type UseAccountTypeBrowseOptions = {
  accountType: NonNullable<AccountSearch["account_type"]>;
  searchBrowse: (params: SearchParams, signal?: AbortSignal) => ReturnType<typeof searchAccounts>;
};

/**
 * Shared browse state for account-type ticket pages (incomes, banks, etc.).
 */
export function useAccountTypeBrowse({ accountType, searchBrowse }: UseAccountTypeBrowseOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBoundary } = useErrorBoundary();

  const urlParams = useMemo(() => parseAccountBrowseUrlParams(searchParams), [searchParams]);

  const [data, setData] = useState<Account[]>([]);
  const [sortKey, setSortKey] = useState(urlParams.sortKey || DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(urlParams.sortMethod);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const accountSearchForm = useForm<AccountSearch>({
    defaultValues: {
      account_type: accountType,
      ...(urlParams.filters ?? {}),
    },
  });

  async function getData(searchData: AccountSearch): Promise<void> {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    try {
      const sort = buildAccountSort(sortKey, sortMethod);
      const payload: SearchParams = {
        filter: {
          account_type: accountType,
          ...deleteEmptyFieldsFromObject(searchData),
        },
        sort: [sort],
      };
      const accounts = await searchBrowse(payload, abortControllerRef.current.signal);
      setData(accounts.data);
      setIsLoading(false);
    } catch (error: unknown) {
      if (error instanceof CanceledError) {
        return;
      }
      handleErrorWithBoundary(error, showBoundary);
      setIsLoading(false);
    }
  }

  const onSearch: SubmitHandler<AccountSearch> = (formData) => {
    syncAccountBrowseUrlParams(setSearchParams, formData, sortKey, sortMethod);
    getData(formData);
  };

  useEntityBrowse({
    form: accountSearchForm,
    sortKey,
    sortMethod,
    onSearch,
  });

  function handleSortKeyChanged(value: string) {
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function reset() {
    const values = setAllValuesToNull(accountSearchForm.getValues());

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        accountSearchForm.setValue(key as keyof AccountSearch, values[key as keyof AccountSearch]);
      }
    }

    accountSearchForm.setValue("account_type", accountType);
  }

  return {
    accountSearchForm,
    data,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    reset,
  };
}
